// Utilities for the Freehand-Draw workspace.
//
// A "stroke" is an ordered list of 3D points that all live on the unit sphere.
// On commit, strokes become panels: each closed stroke turns into one face
// whose vertex list is that stroke's points.

import polygonClipping from 'polygon-clipping';

const EPS = 1e-6;

const norm = (v) => {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
};

const angleBetween = (a, b) => {
  const d = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  return Math.acos(d);
};

// ---------- Snap a point to nearby panel vertices or edges ----------
// Returns { point, kind, panelId, index } if within `thresholdRad`, else null.
// `kind` is either `"vertex"` (snap to a panel corner) or `"edge"` (snap to
// the closest point along a great-circle edge). Vertex snaps win over edge
// snaps when both are within range so users can easily seal seams.
// Pass `exclude = { panelId, index }` to skip a specific vertex — used while
// dragging so a moving handle never latches onto its own current position but
// can still snap to sibling copies of itself.
export function findSnap(p, panels, thresholdRad = (5 * Math.PI) / 180, exclude = null) {
  if (!p || !panels || !panels.length) return null;
  let bestVertex = null;
  let bestEdge = null;

  for (const panel of panels) {
    const pts = panel.points;
    const n = pts.length;
    const isExcludedPanel = exclude && exclude.panelId === panel.id;
    for (let i = 0; i < n; i++) {
      // Vertex candidate.
      if (!(isExcludedPanel && exclude.index === i)) {
        const av = angleBetween(p, pts[i]);
        if (av < thresholdRad && (!bestVertex || av < bestVertex.dist)) {
          bestVertex = {
            dist: av,
            point: [pts[i][0], pts[i][1], pts[i][2]],
            kind: 'vertex',
            panelId: panel.id,
            index: i,
          };
        }
      }
      // Edge candidate (great-circle arc from pts[i] to pts[i+1]).
      const j = (i + 1) % n;
      if (isExcludedPanel && (exclude.index === i || exclude.index === j)) continue;
      const a = pts[i];
      const b = pts[j];
      const snap = closestOnArc(p, a, b);
      if (!snap) continue;
      if (snap.dist < thresholdRad && (!bestEdge || snap.dist < bestEdge.dist)) {
        bestEdge = {
          dist: snap.dist,
          point: snap.point,
          kind: 'edge',
          panelId: panel.id,
          index: i,
        };
      }
    }
  }

  // Prefer vertex snaps whenever they exist and their distance is competitive
  // with the best edge snap (within 1.5× so an edge that's a hair closer
  // doesn't beat a nearby corner).
  if (bestVertex && (!bestEdge || bestVertex.dist <= bestEdge.dist * 1.5)) return bestVertex;
  return bestEdge;
}

// Closest point on the great-circle arc AB to P. Returns null if AB is
// degenerate. Angular distance is returned in `dist`.
function closestOnArc(p, a, b) {
  const nRaw = [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
  const nl = Math.hypot(nRaw[0], nRaw[1], nRaw[2]);
  if (nl < EPS) return null;
  const nx = nRaw[0] / nl, ny = nRaw[1] / nl, nz = nRaw[2] / nl;
  // Project p onto the great-circle plane, then normalize back to sphere.
  const d = p[0] * nx + p[1] * ny + p[2] * nz;
  const px = p[0] - d * nx;
  const py = p[1] - d * ny;
  const pz = p[2] - d * nz;
  const pl = Math.hypot(px, py, pz);
  if (pl < EPS) return null;
  const q = [px / pl, py / pl, pz / pl];
  // Check q lies between a and b on the arc: |ab|_ang > |aq|_ang + |qb|_ang − ε
  const ab = angleBetween(a, b);
  const aq = angleBetween(a, q);
  const qb = angleBetween(q, b);
  if (aq + qb > ab + 1e-3) return null; // outside arc — endpoints handle it
  return { point: q, dist: angleBetween(p, q) };
}


// ---------- Chaikin smoothing (closed loops) ----------
// One iteration replaces every edge (P_i, P_{i+1}) with two points at 1/4 and
// 3/4 along the edge, then re-projects each onto the unit sphere. This tends
// to a C^1 curve while staying on the ball.
export function chaikinSmoothClosed(points, iterations = 2) {
  let pts = points.slice();
  for (let it = 0; it < iterations; it++) {
    const out = [];
    const n = pts.length;
    for (let i = 0; i < n; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % n];
      const q = norm([
        0.75 * a[0] + 0.25 * b[0],
        0.75 * a[1] + 0.25 * b[1],
        0.75 * a[2] + 0.25 * b[2],
      ]);
      const r = norm([
        0.25 * a[0] + 0.75 * b[0],
        0.25 * a[1] + 0.75 * b[1],
        0.25 * a[2] + 0.75 * b[2],
      ]);
      out.push(q, r);
    }
    pts = out;
  }
  return pts;
}

// ---------- Resample a closed loop to uniform arc-length spacing ----------
// spacingRad = target great-circle arc between consecutive vertices (radians).
export function resampleClosed(points, spacingRad) {
  const n = points.length;
  if (n < 3) return points.slice();
  // Cumulative arc lengths.
  const seg = new Array(n);
  let total = 0;
  for (let i = 0; i < n; i++) {
    seg[i] = angleBetween(points[i], points[(i + 1) % n]);
    total += seg[i];
  }
  if (total < EPS) return points.slice();
  const count = Math.max(6, Math.round(total / spacingRad));
  const step = total / count;
  const out = [];
  let acc = 0;
  let target = 0;
  let i = 0;
  out.push(points[0]);
  for (let k = 1; k < count; k++) {
    target = k * step;
    while (i < n && acc + seg[i] < target) {
      acc += seg[i];
      i++;
    }
    const t = seg[i] > EPS ? (target - acc) / seg[i] : 0;
    const a = points[i];
    const b = points[(i + 1) % n];
    // Slerp on the sphere.
    const w = angleBetween(a, b);
    let p;
    if (w < EPS) {
      p = [a[0], a[1], a[2]];
    } else {
      const s = Math.sin(w);
      const w1 = Math.sin((1 - t) * w) / s;
      const w2 = Math.sin(t * w) / s;
      p = norm([
        a[0] * w1 + b[0] * w2,
        a[1] * w1 + b[1] * w2,
        a[2] * w1 + b[2] * w2,
      ]);
    }
    out.push(p);
  }
  return out;
}

// ---------- Remove near-duplicate consecutive points ----------
export function dedupeConsecutive(points, minAngleRad) {
  const out = [];
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const prev = out.length ? out[out.length - 1] : points[n - 1];
    if (angleBetween(prev, points[i]) >= minAngleRad) out.push(points[i]);
  }
  // Ensure closing edge is also long enough.
  while (out.length >= 3 && angleBetween(out[out.length - 1], out[0]) < minAngleRad) {
    out.pop();
  }
  return out;
}

// ---------- Total area of a spherical polygon (steradians) ----------
// Uses the girard's-excess-like formula: sum over edges of
// atan2(det(a,b,ref), 1 + a·b + b·ref + ref·a), where `ref` is the polygon
// centroid direction. Robust for the small, roughly convex panels the user
// draws freehand.
export function sphericalArea(points) {
  const n = points.length;
  if (n < 3) return 0;
  // Centroid direction (unit vector).
  const c = [0, 0, 0];
  for (const p of points) {
    c[0] += p[0];
    c[1] += p[1];
    c[2] += p[2];
  }
  const cl = Math.hypot(c[0], c[1], c[2]) || 1;
  const ref = [c[0] / cl, c[1] / cl, c[2] / cl];
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    // Triple product det(a,b,ref)
    const det =
      a[0] * (b[1] * ref[2] - b[2] * ref[1]) -
      a[1] * (b[0] * ref[2] - b[2] * ref[0]) +
      a[2] * (b[0] * ref[1] - b[1] * ref[0]);
    const dab = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    const dbr = b[0] * ref[0] + b[1] * ref[1] + b[2] * ref[2];
    const dra = ref[0] * a[0] + ref[1] * a[1] + ref[2] * a[2];
    sum += Math.atan2(det, 1 + dab + dbr + dra);
  }
  return Math.abs(2 * sum);
}

// ---------- Cleanup pipeline for one closed stroke ----------
// - dedupe raw points (skip anything closer than 0.5°)
// - Chaikin smooth (2 iterations) — skipped when smoothIterations = 0
// - resample to ~4° spacing — skipped when targetSpacingDeg is null/0
// - drop tiny slivers again after resample
// Returns null if the resulting panel is too small or degenerate.
export function cleanStroke(rawPoints, {
  minAngleDeg = 0.5,
  targetSpacingDeg = 4,
  smoothIterations = 2,
  minAreaSr = 0.002,
} = {}) {
  if (!rawPoints || rawPoints.length < 3) return null;
  const deduped = dedupeConsecutive(rawPoints, (minAngleDeg * Math.PI) / 180);
  if (deduped.length < 3) return null;
  const smoothed = smoothIterations > 0
    ? chaikinSmoothClosed(deduped, smoothIterations)
    : deduped;
  const resampled = targetSpacingDeg
    ? resampleClosed(smoothed, (targetSpacingDeg * Math.PI) / 180)
    : smoothed;
  const final = dedupeConsecutive(resampled, (minAngleDeg * 2 * Math.PI) / 180);
  if (final.length < 3) return null;
  if (sphericalArea(final) < minAreaSr) return null;
  return final;
}

// ---------- Symmetry replicas ----------
// Each returns a list of transformed point-lists (including the original).

const negate = (p) => [-p[0], -p[1], -p[2]];
const reflectY = (p) => [p[0], -p[1], p[2]];

function rotY(p, ang) {
  const c = Math.cos(ang);
  const s = Math.sin(ang);
  return [c * p[0] + s * p[2], p[1], -s * p[0] + c * p[2]];
}

// Icosahedral rotation group (12 vertex-anchored rotations, each with 5-fold
// stabilizer around its axis → 60 total). Rather than hard-code the matrices
// we build them lazily from the canonical icosahedron on demand.
let ICOSA_MATRICES = null;
function icosahedralMatrices() {
  if (ICOSA_MATRICES) return ICOSA_MATRICES;
  const phi = (1 + Math.sqrt(5)) / 2;
  const V = [
    [-1, phi, 0], [1, phi, 0], [-1, -phi, 0], [1, -phi, 0],
    [0, -1, phi], [0, 1, phi], [0, -1, -phi], [0, 1, -phi],
    [phi, 0, -1], [phi, 0, 1], [-phi, 0, -1], [-phi, 0, 1],
  ].map(norm);
  // For each vertex v_i, the rotation that takes v_0 to v_i is a rotation
  // about (v_0 × v_i) by acos(v_0·v_i). Combined with the 5-fold stabilizer
  // around v_0 (rotation by 72°k about v_0), we get 60 distinct rotations.
  const stab5 = [];
  for (let k = 0; k < 5; k++) {
    const ang = (2 * Math.PI * k) / 5;
    stab5.push(rotAxisAngle(V[0], ang));
  }
  const cosets = [];
  cosets.push(identity());
  for (let i = 1; i < V.length; i++) {
    const axis = norm(cross(V[0], V[i]));
    const dp = Math.max(-1, Math.min(1, V[0][0] * V[i][0] + V[0][1] * V[i][1] + V[0][2] * V[i][2]));
    const ang = Math.acos(dp);
    if (Math.hypot(axis[0], axis[1], axis[2]) < 1e-6) {
      // Antipodal — rotate 180° around any orthogonal axis.
      const ortho = norm([1, 0.001, -V[0][0] / (V[0][2] || 1)]);
      cosets.push(rotAxisAngle(ortho, Math.PI));
    } else {
      cosets.push(rotAxisAngle(axis, ang));
    }
  }
  const all = [];
  for (const c of cosets) for (const s of stab5) all.push(mulM(c, s));
  ICOSA_MATRICES = all;
  return ICOSA_MATRICES;
}

// --- Tetrahedral & octahedral groups, built from signed permutations ---
// Both groups sit inside the 48 signed permutations of (x, y, z). A column j
// of the output matrix is `eps[j] * e_{sigma[j]}` (column-major convention,
// matching mulM/applyM in this file).
const SIGNED_PERMS_3 = [
  { sigma: [0, 1, 2], sign:  1 }, // identity
  { sigma: [1, 2, 0], sign:  1 }, // 3-cycle (x→y→z→x)
  { sigma: [2, 0, 1], sign:  1 }, // 3-cycle (x→z→y→x)
  { sigma: [1, 0, 2], sign: -1 }, // swap x,y
  { sigma: [0, 2, 1], sign: -1 }, // swap y,z
  { sigma: [2, 1, 0], sign: -1 }, // swap x,z
];
function signedPermMatrix(sigma, eps) {
  const M = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (let j = 0; j < 3; j++) {
    M[3 * j + sigma[j]] = eps[j];
  }
  return M;
}

// Tetrahedral rotation group T (12 proper rotations preserving the
// tetrahedron with vertices (±1,±1,±1) with an even number of minus signs).
let TET_MATRICES = null;
function tetrahedralMatrices() {
  if (TET_MATRICES) return TET_MATRICES;
  const list = [];
  for (const { sigma, sign } of SIGNED_PERMS_3) {
    if (sign !== 1) continue;
    for (const e0 of [1, -1]) for (const e1 of [1, -1]) for (const e2 of [1, -1]) {
      const minusCount = (e0 < 0 ? 1 : 0) + (e1 < 0 ? 1 : 0) + (e2 < 0 ? 1 : 0);
      if (minusCount % 2 !== 0) continue;
      list.push(signedPermMatrix(sigma, [e0, e1, e2]));
    }
  }
  TET_MATRICES = list; // 3 even perms × 4 even-count sign tuples = 12
  return TET_MATRICES;
}
// Reflection cosets completing T → T_d (24 elements total).
let TET_MIRROR_MATRICES = null;
function tetrahedralMirrorMatrices() {
  if (TET_MIRROR_MATRICES) return TET_MIRROR_MATRICES;
  const list = [];
  for (const { sigma, sign } of SIGNED_PERMS_3) {
    if (sign !== -1) continue;
    for (const e0 of [1, -1]) for (const e1 of [1, -1]) for (const e2 of [1, -1]) {
      const minusCount = (e0 < 0 ? 1 : 0) + (e1 < 0 ? 1 : 0) + (e2 < 0 ? 1 : 0);
      if (minusCount % 2 !== 0) continue;
      list.push(signedPermMatrix(sigma, [e0, e1, e2]));
    }
  }
  TET_MIRROR_MATRICES = list; // 3 odd perms × 4 even-count sign tuples = 12
  return TET_MIRROR_MATRICES;
}

// Chiral octahedral rotation group O (24 proper rotations preserving the
// cube / its dual octahedron).
let OCT_MATRICES = null;
function octahedralMatrices() {
  if (OCT_MATRICES) return OCT_MATRICES;
  const list = [];
  for (const { sigma, sign } of SIGNED_PERMS_3) {
    for (const e0 of [1, -1]) for (const e1 of [1, -1]) for (const e2 of [1, -1]) {
      const prod = e0 * e1 * e2;
      if (sign * prod !== 1) continue; // det = +1
      list.push(signedPermMatrix(sigma, [e0, e1, e2]));
    }
  }
  OCT_MATRICES = list; // 24
  return OCT_MATRICES;
}
// Reflection cosets completing O → O_h (48 elements total).
let OCT_MIRROR_MATRICES = null;
function octahedralMirrorMatrices() {
  if (OCT_MIRROR_MATRICES) return OCT_MIRROR_MATRICES;
  const list = [];
  for (const { sigma, sign } of SIGNED_PERMS_3) {
    for (const e0 of [1, -1]) for (const e1 of [1, -1]) for (const e2 of [1, -1]) {
      const prod = e0 * e1 * e2;
      if (sign * prod !== -1) continue; // det = -1
      list.push(signedPermMatrix(sigma, [e0, e1, e2]));
    }
  }
  OCT_MIRROR_MATRICES = list; // 24 reflections
  return OCT_MIRROR_MATRICES;
}

function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}
function identity() { return [1, 0, 0, 0, 1, 0, 0, 0, 1]; }
function rotAxisAngle(ax, angle) {
  const [x, y, z] = ax;
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const t = 1 - c;
  return [
    t * x * x + c,       t * y * x + s * z,  t * z * x - s * y,
    t * x * y - s * z,   t * y * y + c,      t * z * y + s * x,
    t * x * z + s * y,   t * y * z - s * x,  t * z * z + c,
  ];
}
// Row-major-ish column-major flat matrices (same convention as symmetry.js).
function mulM(A, B) {
  const out = new Array(9).fill(0);
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++)
      for (let k = 0; k < 3; k++)
        out[3 * j + i] += A[3 * k + i] * B[3 * j + k];
  return out;
}
export function applyM(M, p) {
  return [
    M[0] * p[0] + M[3] * p[1] + M[6] * p[2],
    M[1] * p[0] + M[4] * p[1] + M[7] * p[2],
    M[2] * p[0] + M[5] * p[1] + M[8] * p[2],
  ];
}
// Transpose = inverse for orthogonal matrices (rotations and pure reflections).
export function transposeM(M) {
  return [M[0], M[3], M[6], M[1], M[4], M[7], M[2], M[5], M[8]];
}

// Return an array of symmetry transforms as {matrix, matrixInv, reversed}
// records. `reversed` means the transform flips orientation, so the caller
// reverses the point order to keep polygon winding consistent.
//
// `mirror=true` enables the mirror-coset extension for the groups that
// support it (tetrahedral, octahedral, ring). Icosahedral is intentionally
// rotation-only to cap replicas at 60 per stroke. For all other modes the
// flag is a no-op.
export function symmetryTransforms(mode, ringN = 6, mirror = false) {
  switch (mode) {
    case 'antipodal': {
      const neg = [-1, 0, 0, 0, -1, 0, 0, 0, -1];
      return [
        { matrix: identity(), matrixInv: identity(), reversed: false },
        // p → −p is orientation-reversing in 3D (det = −1) but we keep the
        // point order because the panel already lives on the opposite hemi-
        // sphere and DoubleSide rendering + earcut winding-fix handle it.
        { matrix: neg, matrixInv: neg, reversed: false },
      ];
    }
    case 'mirrorX': {
      const refX = [-1, 0, 0, 0, 1, 0, 0, 0, 1];
      return [
        { matrix: identity(), matrixInv: identity(), reversed: false },
        { matrix: refX, matrixInv: refX, reversed: true },
      ];
    }
    case 'mirrorY':
    case 'mirror': { // legacy alias
      const refY = [1, 0, 0, 0, -1, 0, 0, 0, 1];
      return [
        { matrix: identity(), matrixInv: identity(), reversed: false },
        { matrix: refY, matrixInv: refY, reversed: true },
      ];
    }
    case 'mirrorZ': {
      const refZ = [1, 0, 0, 0, 1, 0, 0, 0, -1];
      return [
        { matrix: identity(), matrixInv: identity(), reversed: false },
        { matrix: refZ, matrixInv: refZ, reversed: true },
      ];
    }
    case 'ring': {
      const list = [];
      const n = Math.max(2, Math.min(12, Math.round(ringN)));
      // When `mirror` is on for Ring, every other replica is additionally
      // flipped top↔bottom (Y → −Y). Reflection in XZ commutes with rotation
      // around Y so it just alternates the orientation of consecutive
      // copies. Total replica count stays `n` — unlike the mirror cosets
      // for the polyhedral groups which double the count.
      const flipY = [1, 0, 0, 0, -1, 0, 0, 0, 1];
      for (let k = 0; k < n; k++) {
        const ang = (2 * Math.PI * k) / n;
        const R = rotAxisAngle([0, 1, 0], ang);
        const flipped = mirror && (k % 2 === 1);
        const M = flipped ? mulM(R, flipY) : R;
        list.push({ matrix: M, matrixInv: transposeM(M), reversed: flipped });
      }
      return list;
    }
    case 'tetrahedral': {
      const mats = tetrahedralMatrices();
      const base = mats.map((M) => ({ matrix: M, matrixInv: transposeM(M), reversed: false }));
      if (!mirror) return base;
      const refl = tetrahedralMirrorMatrices()
        .map((M) => ({ matrix: M, matrixInv: transposeM(M), reversed: true }));
      return base.concat(refl); // 24 total
    }
    case 'octahedral': {
      const mats = octahedralMatrices();
      const base = mats.map((M) => ({ matrix: M, matrixInv: transposeM(M), reversed: false }));
      if (!mirror) return base;
      const refl = octahedralMirrorMatrices()
        .map((M) => ({ matrix: M, matrixInv: transposeM(M), reversed: true }));
      return base.concat(refl); // 48 total
    }
    case 'icosahedral':
    case 'full': {
      // Rotation-only, 60 replicas. Mirror is intentionally unsupported here
      // to keep per-stroke replica cost tractable.
      const mats = icosahedralMatrices();
      return mats.map((M) => ({ matrix: M, matrixInv: transposeM(M), reversed: false }));
    }
    case 'none':
    default:
      return [{ matrix: identity(), matrixInv: identity(), reversed: false }];
  }
}

// Back-compat: return an array of functions. Used by the live preview render.
export function symmetryReplicas(mode, ringN = 6, mirror = false) {
  return symmetryTransforms(mode, ringN, mirror).map(({ matrix }) => (p) => applyM(matrix, p));
}

// Apply symmetry to a stroke, returning replicas AND the per-replica transform
// info so callers can propagate edits later. Each result is:
// `{ points, matrix, matrixInv, reversed, symIdx }`.
export function replicateStroke(points, mode, ringN = 6, mirror = false) {
  const transforms = symmetryTransforms(mode, ringN, mirror);
  const raw = transforms.map((t, idx) => {
    let pts = points.map((p) => norm(applyM(t.matrix, p)));
    if (t.reversed) pts = pts.slice().reverse();
    return { points: pts, matrix: t.matrix, matrixInv: t.matrixInv, reversed: t.reversed, symIdx: idx };
  });
  const polyGroup = (mode === 'icosahedral' || mode === 'full' || mode === 'octahedral' || mode === 'tetrahedral');
  return dedupeReplicas(raw, polyGroup ? 0.02 : 0.005);
}

// Returns how many distinct replicas `mode` currently produces. Used by the
// Right Inspector to show a live "N copies" badge beside the symmetry label.
export function symmetryReplicaCount(mode, ringN = 6, mirror = false) {
  // Count without running the stroke dedupe pass — these numbers describe
  // the ambient symmetry group size, not an edge-case dedupe outcome.
  return symmetryTransforms(mode, ringN, mirror).length;
}

// Which modes have a meaningful "with/without mirror" variant.
export function symmetrySupportsMirror(mode) {
  return mode === 'tetrahedral' || mode === 'octahedral' || mode === 'ring';
}

// Rotation / mirror axes to visualise as dashed overlay sticks on the ball,
// one entry per unique axis direction. Each entry carries the axis unit
// vector plus the rotation order (`fold`) — the UI renders 2/3/4/5-fold
// axes in different hues so the designer can tell them apart at a glance.
// Axes that pass through the ball origin are returned as a single unit
// vector; the viewport renders them as a long line through the sphere so
// both poles poke out and are visible against the mesh.
export function symmetryGuideAxes(mode, ringN = 6) {
  const phi = (1 + Math.sqrt(5)) / 2;
  const push = (list, axis, fold) => {
    const L = Math.hypot(axis[0], axis[1], axis[2]) || 1;
    const u = [axis[0] / L, axis[1] / L, axis[2] / L];
    // Dedup by direction — treat (v, −v) as the same axis.
    for (const existing of list) {
      const d = existing.axis[0] * u[0] + existing.axis[1] * u[1] + existing.axis[2] * u[2];
      if (Math.abs(d) > 0.999) return;
    }
    list.push({ axis: u, fold });
  };
  const out = [];
  switch (mode) {
    case 'mirrorX':
      push(out, [1, 0, 0], 2);
      return out;
    case 'mirrorY':
      push(out, [0, 1, 0], 2);
      return out;
    case 'mirrorZ':
      push(out, [0, 0, 1], 2);
      return out;
    case 'ring':
      push(out, [0, 1, 0], Math.max(2, Math.min(12, Math.round(ringN))));
      return out;
    case 'tetrahedral': {
      // 4 vertex axes (3-fold)
      [[1, 1, 1], [-1, -1, 1], [-1, 1, -1], [1, -1, -1]].forEach((v) => push(out, v, 3));
      // 3 edge-midpoint axes (2-fold) = coordinate axes
      [[1, 0, 0], [0, 1, 0], [0, 0, 1]].forEach((v) => push(out, v, 2));
      return out;
    }
    case 'octahedral': {
      // 3 face axes (4-fold) along X / Y / Z
      [[1, 0, 0], [0, 1, 0], [0, 0, 1]].forEach((v) => push(out, v, 4));
      // 4 vertex axes (3-fold) = cube body diagonals
      [[1, 1, 1], [-1, 1, 1], [1, -1, 1], [1, 1, -1]].forEach((v) => push(out, v, 3));
      // 6 edge axes (2-fold) through cube edge midpoints
      [
        [1, 1, 0], [1, -1, 0],
        [1, 0, 1], [1, 0, -1],
        [0, 1, 1], [0, 1, -1],
      ].forEach((v) => push(out, v, 2));
      return out;
    }
    case 'icosahedral':
    case 'full': {
      // 6 vertex axes (5-fold) — 12 vertices of the icosahedron / 2
      const verts = [
        [-1, phi, 0], [1, phi, 0], [-1, -phi, 0], [1, -phi, 0],
        [0, -1, phi], [0, 1, phi], [0, -1, -phi], [0, 1, -phi],
        [phi, 0, -1], [phi, 0, 1], [-phi, 0, -1], [-phi, 0, 1],
      ];
      verts.forEach((v) => push(out, v, 5));
      // 10 face axes (3-fold) — face centroids of the icosahedron
      // Build the icosahedron face list by picking all triples whose
      // pairwise edge length ≤ 2 (standard construction).
      const normalisedVerts = verts.map((v) => {
        const L = Math.hypot(v[0], v[1], v[2]) || 1;
        return [v[0] / L, v[1] / L, v[2] / L];
      });
      // Icosahedron edge length (unit sphere): 2 * sin(π/5) ≈ 1.1756
      const edgeLen = 2 * Math.sin(Math.PI / 5);
      const edgeTol = edgeLen * 1.05;
      const faces = [];
      for (let i = 0; i < 12; i++) {
        for (let j = i + 1; j < 12; j++) {
          for (let k = j + 1; k < 12; k++) {
            const d = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
            const a = normalisedVerts[i];
            const b = normalisedVerts[j];
            const c = normalisedVerts[k];
            if (d(a, b) < edgeTol && d(b, c) < edgeTol && d(a, c) < edgeTol) {
              faces.push([a, b, c]);
            }
          }
        }
      }
      faces.forEach(([a, b, c]) => {
        push(out, [a[0] + b[0] + c[0], a[1] + b[1] + c[1], a[2] + b[2] + c[2]], 3);
      });
      // 15 edge axes (2-fold) — edge midpoints
      for (let i = 0; i < 12; i++) {
        for (let j = i + 1; j < 12; j++) {
          const a = normalisedVerts[i];
          const b = normalisedVerts[j];
          const d = Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
          if (d < edgeTol) {
            push(out, [a[0] + b[0], a[1] + b[1], a[2] + b[2]], 2);
          }
        }
      }
      return out;
    }
    case 'antipodal':
    case 'none':
    default:
      return out;
  }
}

// Drop replica polygons whose centroid is within `tol` of an already-seen
// centroid. Prevents overdraw from replicating a stroke that already lies on
// the symmetry axis.
function dedupeReplicas(list, tol) {
  const seen = [];
  const kept = [];
  for (const r of list) {
    const c = centroid(r.points);
    if (seen.some((s) => angleBetween(s, c) < tol)) continue;
    seen.push(c);
    kept.push(r);
  }
  return kept;
}

function centroid(pts) {
  const acc = [0, 0, 0];
  for (const p of pts) {
    acc[0] += p[0];
    acc[1] += p[1];
    acc[2] += p[2];
  }
  return norm(acc);
}

// ---------- Grid points on the sphere (for snap-to-grid) ----------
// Cached per (type, density) so hover-time snap checks don't rebuild the mesh.
let GRID_CACHE = { key: null, points: null };
export function generateGridPoints(type, density) {
  const key = `${type}:${density}`;
  if (GRID_CACHE.key === key) return GRID_CACHE.points;
  let points = [];
  if (type === 'axes') {
    // Three orthogonal great circles — one in each of the XY / YZ / XZ
    // planes — so users can align panels symmetrically to any cardinal axis
    // of the ball. `density` controls how many discrete snap points live on
    // each circle (density * 8 evenly-spaced points per circle).
    const perCircle = Math.max(8, Math.round(density) * 8);
    for (let i = 0; i < perCircle; i++) {
      const a = (2 * Math.PI * i) / perCircle;
      const c = Math.cos(a), s = Math.sin(a);
      points.push([c, s, 0]);   // XY-plane circle (spans X & Y axes)
      points.push([0, c, s]);   // YZ-plane circle (spans Y & Z axes)
      points.push([c, 0, s]);   // XZ-plane circle (spans X & Z axes)
    }
  } else if (type === 'latlon') {
    const latSteps = Math.max(3, Math.round(density) * 2); // e.g. density 4 → 8 latitudes
    const lonSteps = Math.max(6, Math.round(density) * 4);
    for (let i = 1; i < latSteps; i++) {
      const phi = (Math.PI * i) / latSteps - Math.PI / 2; // -π/2 → π/2
      const cy = Math.sin(phi);
      const r = Math.cos(phi);
      for (let j = 0; j < lonSteps; j++) {
        const theta = (2 * Math.PI * j) / lonSteps;
        points.push([r * Math.cos(theta), cy, r * Math.sin(theta)]);
      }
    }
    // Add poles too
    points.push([0, 1, 0]);
    points.push([0, -1, 0]);
  } else if (type === 'tri' || type === 'hex') {
    // Subdivide the icosahedron `density` times.
    const phi = (1 + Math.sqrt(5)) / 2;
    let verts = [
      [-1, phi, 0], [1, phi, 0], [-1, -phi, 0], [1, -phi, 0],
      [0, -1, phi], [0, 1, phi], [0, -1, -phi], [0, 1, -phi],
      [phi, 0, -1], [phi, 0, 1], [-phi, 0, -1], [-phi, 0, 1],
    ].map(norm);
    let faces = [
      [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
      [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
      [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
      [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1],
    ];
    const depth = Math.max(0, Math.min(3, Math.round(density) - 1));
    for (let d = 0; d < depth; d++) {
      const midCache = new Map();
      const midpoint = (a, b) => {
        const key = a < b ? `${a}_${b}` : `${b}_${a}`;
        if (midCache.has(key)) return midCache.get(key);
        const va = verts[a], vb = verts[b];
        const m = norm([va[0] + vb[0], va[1] + vb[1], va[2] + vb[2]]);
        const idx = verts.length;
        verts.push(m);
        midCache.set(key, idx);
        return idx;
      };
      const nextFaces = [];
      for (const [a, b, c] of faces) {
        const ab = midpoint(a, b);
        const bc = midpoint(b, c);
        const ca = midpoint(c, a);
        nextFaces.push([a, ab, ca], [b, bc, ab], [c, ca, bc], [ab, bc, ca]);
      }
      faces = nextFaces;
    }
    if (type === 'tri') {
      // Every subdivided vertex is a grid node.
      points = verts;
    } else {
      // Hex grid = face centres of the subdivided icosahedron (dual polyhedron
      // vertices). Face centres form a Goldberg polyhedron whose faces are
      // pentagons and hexagons — perfect anchors for classic soccer panels.
      const seen = new Set();
      for (const [a, b, c] of faces) {
        const va = verts[a], vb = verts[b], vc = verts[c];
        const m = norm([va[0] + vb[0] + vc[0], va[1] + vb[1] + vc[1], va[2] + vb[2] + vc[2]]);
        const key = `${m[0].toFixed(4)}_${m[1].toFixed(4)}_${m[2].toFixed(4)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        points.push(m);
      }
    }
  }
  GRID_CACHE = { key, points };
  return points;
}

// Nearest grid intersection to p within `thresholdRad`. Returns a snap record
// compatible with `findSnap` shape so callers can treat it uniformly.
export function findGridSnap(p, gridPoints, thresholdRad = (4 * Math.PI) / 180) {
  if (!p || !gridPoints || !gridPoints.length) return null;
  let best = null;
  for (let i = 0; i < gridPoints.length; i++) {
    const gp = gridPoints[i];
    const d = angleBetween(p, gp);
    if (d < thresholdRad && (!best || d < best.dist)) {
      best = { dist: d, point: [gp[0], gp[1], gp[2]], kind: 'grid', panelId: null, index: i };
    }
  }
  return best;
}

// Merge a panel-snap and a grid-snap, picking the best. Vertex > edge > grid
// with a 1.5× tolerance so a slightly-closer grid point doesn't beat a nearby
// panel corner.
export function pickBestSnap(panelSnap, gridSnap) {
  if (!panelSnap) return gridSnap;
  if (!gridSnap) return panelSnap;
  if (panelSnap.kind === 'vertex') return panelSnap;
  if (panelSnap.dist <= gridSnap.dist * 1.5) return panelSnap;
  return gridSnap;
}

// ---------- Regular-polygon starter shapes on the sphere ----------
// Builds an N-gon (or 32-gon "circle") centred at `center` with angular
// radius `radius` (radians). Vertices are placed by rotating an angular
// offset `radius` away from `center` in the tangent plane, so the shape's
// size is measured as an arc distance on the ball. Winding is CCW when
// viewed from outside the ball.
export function buildShapePolygon(center, radius, kind, aspect = 2) {
  const specs = {
    triangle: { n: 3, offset: Math.PI / 2 },
    square:   { n: 4, offset: Math.PI / 4 },
    diamond:  { n: 4, offset: Math.PI / 2 },
    pentagon: { n: 5, offset: Math.PI / 2 },
    hexagon:  { n: 6, offset: 0 },
    circle:   { n: 48, offset: 0 },
  };
  // Rectangle of width:height = aspect — corners on the drag radius circle.
  const rt = Math.atan2(1, aspect > 0 ? aspect : 2);
  specs.rectangle = { n: 4, thetas: [rt, Math.PI - rt, Math.PI + rt, 2 * Math.PI - rt] };
  const spec = specs[kind];
  if (!spec || !center || !(radius > 0)) return null;
  // Tangent basis at center. `ex` points around latitude, `ey` toward pole.
  const c = center;
  const up = Math.abs(c[1]) < 0.95 ? [0, 1, 0] : [1, 0, 0];
  let ex = cross(up, c);
  const el = Math.hypot(ex[0], ex[1], ex[2]) || 1;
  ex = [ex[0] / el, ex[1] / el, ex[2] / el];
  const ey = cross(c, ex);
  const cosR = Math.cos(radius);
  const sinR = Math.sin(radius);
  const out = [];
  for (let i = 0; i < spec.n; i++) {
    const theta = spec.thetas ? spec.thetas[i] : spec.offset + (2 * Math.PI * i) / spec.n;
    const dx = Math.cos(theta);
    const dy = Math.sin(theta);
    const p = [
      cosR * c[0] + sinR * (dx * ex[0] + dy * ey[0]),
      cosR * c[1] + sinR * (dx * ex[1] + dy * ey[1]),
      cosR * c[2] + sinR * (dx * ex[2] + dy * ey[2]),
    ];
    out.push(norm(p));
  }
  return out;
}

// ---------- Boolean subtract on the sphere (Punch-through mode) ----------
// Uses gnomonic projection to a tangent plane centred between the two panels'
// centroids, runs polygon-clipping's difference, then lifts every returned
// polygon back to the sphere. Returns an array of `{ outer, holes }` pieces:
//   - Empty array = existing fully consumed by the cutter.
//   - null = no overlap (caller should keep the panel untouched).
//   - Length-1 = one piece (usually the trimmed original or a donut with holes).
//   - Length-2+ = the cut split the panel into multiple pieces.

function centroidUnit(points) {
  const acc = [0, 0, 0];
  for (const p of points) { acc[0] += p[0]; acc[1] += p[1]; acc[2] += p[2]; }
  return norm(acc);
}

function tangentBasis(c) {
  const up = Math.abs(c[1]) < 0.95 ? [0, 1, 0] : [1, 0, 0];
  let ex = cross(up, c);
  const el = Math.hypot(ex[0], ex[1], ex[2]) || 1;
  ex = [ex[0] / el, ex[1] / el, ex[2] / el];
  const ey = cross(c, ex);
  return { ex, ey };
}

function projectRing(ring3d, center, basis) {
  const { ex, ey } = basis;
  const out = [];
  for (const p of ring3d) {
    const s = p[0] * center[0] + p[1] * center[1] + p[2] * center[2];
    if (s < 1e-3) return null; // point on wrong hemisphere — projection would blow up
    const inv = 1 / s;
    out.push([
      (p[0] * ex[0] + p[1] * ex[1] + p[2] * ex[2]) * inv,
      (p[0] * ey[0] + p[1] * ey[1] + p[2] * ey[2]) * inv,
    ]);
  }
  out.push(out[0]); // polygon-clipping accepts either, but explicit close is safer
  return out;
}

function unprojectRing(ring2d, center, basis) {
  const { ex, ey } = basis;
  const out = [];
  for (let i = 0; i < ring2d.length; i++) {
    const [u, v] = ring2d[i];
    const p = norm([
      center[0] + u * ex[0] + v * ey[0],
      center[1] + u * ex[1] + v * ey[1],
      center[2] + u * ex[2] + v * ey[2],
    ]);
    // Drop the closing duplicate polygon-clipping appends.
    if (i === ring2d.length - 1 && out.length >= 3) {
      const f = out[0];
      if (Math.hypot(p[0] - f[0], p[1] - f[1], p[2] - f[2]) < 1e-6) continue;
    }
    out.push(p);
  }
  return out;
}

export function subtractPanel(existingOuter, existingHoles, cutter) {
  const ec = centroidUnit(existingOuter);
  const cc = centroidUnit(cutter);
  // Quick reject: centroids more than ~85° apart → no chance of overlap.
  if (ec[0] * cc[0] + ec[1] * cc[1] + ec[2] * cc[2] < 0.1) return null;
  // Tangent plane centred on the midpoint of both centroids.
  const center = centroidUnit([...existingOuter, ...cutter]);
  const basis = tangentBasis(center);
  const existRing = projectRing(existingOuter, center, basis);
  const cutterRing = projectRing(cutter, center, basis);
  if (!existRing || !cutterRing) return null;
  const holes2d = (existingHoles || [])
    .map((h) => projectRing(h, center, basis))
    .filter(Boolean);
  const subject = [[existRing, ...holes2d]];
  const clip = [[cutterRing]];
  let result;
  try {
    result = polygonClipping.difference(subject, clip);
  } catch (e) {
    return null;
  }
  if (!result) return null;
  if (result.length === 0) return []; // fully cut away
  // If the difference returns exactly the input unchanged, no cut happened
  // (no real overlap). Detect: single polygon, same vertex count, no new holes.
  if (result.length === 1 && result[0].length === 1 + holes2d.length &&
      result[0][0].length === existRing.length) {
    // Rough equality check: sum of squared distances between corresponding
    // vertices. If tiny → return null so the caller preserves the original.
    let diff = 0;
    for (let i = 0; i < existRing.length; i++) {
      diff += Math.abs(result[0][0][i][0] - existRing[i][0]) +
              Math.abs(result[0][0][i][1] - existRing[i][1]);
      if (diff > 1e-4) break;
    }
    if (diff < 1e-4) return null;
  }
  return result.map((poly) => {
    const [outer2d, ...holeRings] = poly;
    return {
      outer: unprojectRing(outer2d, center, basis),
      holes: holeRings.map((h) => unprojectRing(h, center, basis)),
    };
  });
}

// ---------- Convert strokes → {vertices, faces, faceHoles} for export ----------
// Each stroke can carry an optional `holes` array (rings of 3D points that
// live inside the outer boundary). `faceHoles[i]` is a parallel array where
// entry i is the list of hole-vertex-index rings for face i.
//
// Vertices are deduplicated by a 4-decimal-place spatial key. This is
// critical for stitch-hole matching in `panelSheet.buildPanelSheet`: when a
// punch-through cut produces a parent panel with an aperture ring AND an
// insert panel whose outer boundary is that same ring, both sides need to
// reference the SAME vertex indices so the shared `edgeHoleCount` map emits
// identical hole counts on both stitch lines.
export function strokesToMesh(strokes) {
  const vertices = [];
  const faces = [];
  const faceHoles = [];
  // Bucket vertices by a coarse spatial grid so duplicate-lookup is O(1)
  // in practice. Bucket size 0.01 on the unit sphere (~0.57° ≈ 1 mm on a
  // 213 mm ball) is well below any real user-drawn spacing but comfortably
  // above polygon-clipping's snap-rounding noise, so a hole ring returned
  // from a boolean subtract and its mating insert-panel outline collapse
  // onto the same vertex indices.
  const EPS = 0.005;         // merge radius in unit-sphere units
  const EPS2 = EPS * EPS;
  const CELL = 0.02;         // grid cell size — must be >= EPS
  const buckets = new Map();
  const bucketKey = (p) =>
    `${Math.round(p[0] / CELL)}|${Math.round(p[1] / CELL)}|${Math.round(p[2] / CELL)}`;
  const idxOf = (p) => {
    const bx = Math.round(p[0] / CELL);
    const by = Math.round(p[1] / CELL);
    const bz = Math.round(p[2] / CELL);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const list = buckets.get(`${bx + dx}|${by + dy}|${bz + dz}`);
          if (!list) continue;
          for (const i of list) {
            const q = vertices[i];
            const ex = p[0] - q[0];
            const ey = p[1] - q[1];
            const ez = p[2] - q[2];
            if (ex * ex + ey * ey + ez * ez < EPS2) return i;
          }
        }
      }
    }
    const i = vertices.length;
    vertices.push([p[0], p[1], p[2]]);
    const k = bucketKey(p);
    let list = buckets.get(k);
    if (!list) { list = []; buckets.set(k, list); }
    list.push(i);
    return i;
  };
  // Collapse consecutive duplicate indices in a ring (can happen when two
  // adjacent stroke samples fall inside the merge radius).
  const collapseRing = (ring) => {
    const out = [];
    for (const i of ring) {
      if (out.length && out[out.length - 1] === i) continue;
      out.push(i);
    }
    while (out.length > 1 && out[0] === out[out.length - 1]) out.pop();
    return out;
  };
  for (const s of strokes) {
    const face = collapseRing(s.points.map(idxOf));
    faces.push(face);
    const holes = (s.holes || [])
      .map((ring) => collapseRing(ring.map(idxOf)))
      .filter((r) => r.length >= 3);
    faceHoles.push(holes);
  }
  // Insert T-junction vertices so every panel sees the same edge subdivisions
  // along a shared seam. Without this pass, if Panel A has 3 corners along a
  // seam (a → m1 → m2 → b) and the adjacent Panel B drew that same seam as
  // one straight edge (a → b), the panelSheet exporter keys A's three
  // sub-edges and B's single edge separately — so each side gets a different
  // stitch-hole count. After this pass both panels share edges (a,m1),
  // (m1,m2), (m2,b) and the shared edge map produces matching holes.
  insertTJunctions(vertices, faces, faceHoles);
  return { vertices, faces, faceHoles };
}

// Walks every face edge and splits it at any vertex that lies on the
// great-circle arc between its endpoints. Mutates `faces` (and the hole
// rings inside `faceHoles`) in place. Idempotent — safe to call once per
// mesh build.
function insertTJunctions(vertices, faces, faceHoles) {
  // Angular tolerance for "vertex lies on this arc". 0.015 rad ≈ 0.86° on
  // a unit sphere — large enough to catch user-drawn snaps (which already
  // merge within 0.005 rad in idxOf) yet tight enough to not spuriously
  // insert far-away vertices.
  const PERP_EPS = 0.012;      // perpendicular distance to the great circle
  const T_EPS = 0.015;         // minimum angular gap from either endpoint

  const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const cross = (a, b) => [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
  const vlen = (v) => Math.hypot(v[0], v[1], v[2]);

  // Freehand meshes rarely exceed ~1k vertices and ~40 panels so an O(V)
  // scan per edge is fine. We tried bucketing on a coarse sphere grid but
  // the chord-midpoint heuristic missed candidates on long arcs whose apex
  // lies far from the chord, so we just scan all vertices here.
  const candidateVerts = (a, b) => {
    const out = [];
    for (let i = 0; i < vertices.length; i++) {
      if (i !== a && i !== b) out.push(i);
    }
    return out;
  };

  const splitRing = (ring) => {
    let changed = false;
    let guard = 0;
    let current = ring.slice();
    // Repeat until a full pass makes no splits — splits can expose new
    // T-junctions on the resulting sub-arcs.
    while (guard++ < 8) {
      let any = false;
      const next = [];
      for (let i = 0; i < current.length; i++) {
        const a = current[i];
        const b = current[(i + 1) % current.length];
        next.push(a);
        const va = vertices[a];
        const vb = vertices[b];
        const n = cross(va, vb);
        const nLen = vlen(n);
        if (nLen < 1e-9) continue;
        const invN = 1 / nLen;
        const dAB = Math.max(-1, Math.min(1, dot(va, vb) / (vlen(va) * vlen(vb))));
        const angleAB = Math.acos(dAB);
        if (angleAB < T_EPS * 2) continue;
        const inserts = [];
        for (const vi of candidateVerts(a, b)) {
          const vv = vertices[vi];
          const perp = Math.abs(dot(vv, n) * invN) / (vlen(vv) || 1);
          if (perp > PERP_EPS) continue;
          const cA = Math.max(-1, Math.min(1, dot(va, vv) / (vlen(va) * vlen(vv))));
          const cB = Math.max(-1, Math.min(1, dot(vb, vv) / (vlen(vb) * vlen(vv))));
          const tA = Math.acos(cA);
          const tB = Math.acos(cB);
          // Must sit *between* a and b on the arc (tA + tB ≈ angleAB)
          if (Math.abs(tA + tB - angleAB) > T_EPS) continue;
          if (tA < T_EPS || tB < T_EPS) continue;
          inserts.push({ vi, t: tA });
        }
        if (!inserts.length) continue;
        inserts.sort((x, y) => x.t - y.t);
        let lastInserted = -1;
        for (const { vi } of inserts) {
          if (vi === lastInserted) continue;
          if (next[next.length - 1] === vi) continue;
          next.push(vi);
          lastInserted = vi;
          any = true;
          changed = true;
        }
      }
      if (!any) break;
      current = next;
    }
    return changed ? current : ring;
  };

  for (let fi = 0; fi < faces.length; fi++) {
    faces[fi] = splitRing(faces[fi]);
    const holes = faceHoles[fi];
    if (holes && holes.length) {
      for (let hi = 0; hi < holes.length; hi++) {
        holes[hi] = splitRing(holes[hi]);
      }
    }
  }
}

export const SCALE_PIVOTS = {
  top: [0, 1, 0], bottom: [0, -1, 0],
  front: [0, 0, 1], back: [0, 0, -1],
  right: [1, 0, 0], left: [-1, 0, 0],
};

// Zoom a point about a pivot direction: angular distance from pivot × k, bearing kept.
export function zoomFromPivot(v, k, c = [0, 1, 0]) {
  const dp = Math.max(-1, Math.min(1, v[0] * c[0] + v[1] * c[1] + v[2] * c[2]));
  const tx = v[0] - dp * c[0], ty = v[1] - dp * c[1], tz = v[2] - dp * c[2];
  const r = Math.hypot(tx, ty, tz);
  if (r < 1e-9) return dp > 0 ? c.slice() : [-c[0], -c[1], -c[2]];
  const newAng = Math.max(0.002, Math.min(Math.PI - 0.02, Math.acos(dp) * k));
  const cA = Math.cos(newAng), sA = Math.sin(newAng) / r;
  return [cA * c[0] + sA * tx, cA * c[1] + sA * ty, cA * c[2] + sA * tz];
}

// A symmetry matrix survives a pivot-zoom only if it keeps the pivot fixed.
export function keepsAxis(M, c = [0, 1, 0]) {
  const m = [M[0] * c[0] + M[1] * c[1] + M[2] * c[2], M[3] * c[0] + M[4] * c[1] + M[5] * c[2], M[6] * c[0] + M[7] * c[1] + M[8] * c[2]];
  return Math.abs(m[0] - c[0]) < 1e-6 && Math.abs(m[1] - c[1]) < 1e-6 && Math.abs(m[2] - c[2]) < 1e-6;
}
